import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import UploadIcon from '@mui/icons-material/Upload';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';

import { useDebriefModal } from 'ff/debrief/modalManager';
import DebriefDialogTitle from 'ff/debrief/components/modals/DebriefDialogTitle';
import { useCurrentTracklog } from 'ff/debrief/hooks';
import FileDropZone from 'ff/debrief/components/imports/FileDropZone';
import FilePreview from 'ff/debrief/components/imports/FilePreview';

const ImportsModal = () => {
    const { isOpen, close } = useDebriefModal('Imports');
    const { setTracklog, importG1000Tracklog } = useCurrentTracklog();
    const [selectedFile, setSelectedFile] = useState<File>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string>(null);

    const handleClose = () => {
        close();
        setSelectedFile(null);
        setUploadError(null);
    };

    const handleFileSelect = (files: FileList) => {
        if (files && files.length > 0) {
            setSelectedFile(files[0]);
            setUploadError(null);
        }
    };

    const handleFileUpload = async () => {
        if (!selectedFile) return;

        setIsUploading(true);
        setUploadError(null);

        try {
            const formData = new FormData();
            formData.append('file', selectedFile);

            const response = await importG1000Tracklog(formData).unwrap();
            setTracklog(response.trackUuid);

            handleClose();
        } catch (error) {
            const message =
                error?.data?.serverResponseData ||
                error?.data?.message ||
                'An unexpected error occurred. Please try again.';
            setUploadError(message);
        } finally {
            setIsUploading(false);
        }
    };

    const handleRemoveFile = () => {
        setSelectedFile(null);
        setUploadError(null);
    };

    return (
        <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
            <DebriefDialogTitle onClose={handleClose}>
                Import G1000 File
            </DebriefDialogTitle>

            <DialogContent>
                {uploadError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        <AlertTitle>Import Failed</AlertTitle>
                        {uploadError}
                    </Alert>
                )}

                {!selectedFile ? (
                    <FileDropZone onFileSelect={handleFileSelect} />
                ) : (
                    <FilePreview
                        file={selectedFile}
                        onRemove={handleRemoveFile}
                    />
                )}
            </DialogContent>

            {selectedFile && (
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button onClick={handleClose} disabled={isUploading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleFileUpload}
                        variant="contained"
                        startIcon={<UploadIcon />}
                        disabled={isUploading}
                    >
                        {isUploading ? 'Importing...' : 'Import File'}
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
};

export default ImportsModal;
